import { useEffect, useRef, useState } from "react";
import { createPortal } from "react-dom";
import { Link } from "react-router";
import type { ActorDTO, Role, Stage, TaskDTO } from "@roomate/core";
import { useActors, useBridge, useTasks } from "../api.ts";
import { Ago, cx, STAGE_LABEL } from "../ui.tsx";

const CLOSED: readonly Stage[] = ["done", "cancelled"];
const CARD_WIDTH = 288;

const ACRONYM_ROLES: Partial<Record<Role, string>> = { qa: "QA", pm: "PM", ux: "UX", ui: "UI" };

export function roleLabel(role: Role): string {
  const acronym = ACRONYM_ROLES[role];
  if (acronym) return acronym;
  const words = role.replace(/_/g, " ");
  return words.charAt(0).toUpperCase() + words.slice(1);
}

/** The rail labels agents without their "-agent" suffix; the card title matches its trigger. */
export function displayName(name: string): string {
  return name.replace(/-agent$/, "");
}

/** Open tasks a person is on: built or reviewed by them or by any agent they own (a deactivated agent's work still awaits its owner). */
export function openTasksFor(actor: ActorDTO, actors: ActorDTO[], tasks: TaskDTO[]): TaskDTO[] {
  const agents = actors.filter((a) => a.kind === "agent" && a.ownerId === actor.id);
  const ids = new Set([actor.id, ...agents.map((a) => a.id)]);
  return tasks.filter((t) => !CLOSED.includes(t.stage) && (ids.has(t.builderId ?? "") || t.reviewerIds.some((id) => ids.has(id))));
}

/** An agent's current task: its live lane wins, else the task it is actively building. Queued or waiting work is not "current". */
export function currentTaskFor(actor: ActorDTO, tasks: TaskDTO[], laneTaskId: string | null): TaskDTO | null {
  if (laneTaskId) return tasks.find((t) => t.id === laneTaskId) ?? null;
  return tasks.find((t) => t.builderId === actor.id && t.stage === "in_progress") ?? null;
}

type TasksState = "loading" | "ready" | "error";

/** "Nothing open" is only true once the task list has loaded; a failed fetch says so instead of loading forever. */
export function tasksMessage(state: TasksState, empty: string): string {
  return state === "ready" ? empty : state === "error" ? "Couldn't load tasks." : "Loading…";
}

function StatusDot({ online }: { online: boolean }) {
  return <span aria-hidden className={cx("inline-block size-1.5 shrink-0 rounded-full", online ? "bg-ok" : "bg-ink-3")} />;
}

/** One line for presence: live now, or when they were last seen. */
function Presence({ online, lastSeenAt }: { online: boolean; lastSeenAt: number | null }) {
  if (online) {
    return (
      <span className="inline-flex items-center gap-1.5 text-ink-2" data-testid="person-card-status" data-online="true">
        <StatusDot online />
        Online now
      </span>
    );
  }
  return (
    <span className="inline-flex items-center gap-1.5 text-ink-3" data-testid="person-card-status" data-online="false">
      <StatusDot online={false} />
      {lastSeenAt === null ? "Not seen yet" : <>Last seen <Ago ts={lastSeenAt} /></>}
    </span>
  );
}

function SectionLabel({ children }: { children: string }) {
  return <h3 className="mb-1.5 font-mono text-[10.5px] uppercase tracking-[0.14em] text-ink-3">{children}</h3>;
}

function TaskLink({ task, onNavigate }: { task: TaskDTO; onNavigate: () => void }) {
  return (
    <li>
      <Link
        to={`/tasks/${task.id}`}
        onClick={onNavigate}
        data-testid="person-card-task"
        className="group flex items-baseline gap-2 rounded-md px-1.5 py-1 -mx-1.5 text-[13px] text-ink hover:bg-paper-2 focus-visible:bg-paper-2 focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-[color:var(--focus)]"
      >
        <span className="font-mono text-[11px] tabular-nums text-ink-3">#{task.number}</span>
        <span className="min-w-0 flex-1 truncate group-hover:underline">{task.title}</span>
        <span className="shrink-0 text-[11px] text-ink-3">{STAGE_LABEL[task.stage]}</span>
      </Link>
    </li>
  );
}

export type PersonCardProps = {
  /** The person or agent the rail avatar stands for. */
  actorId: string;
  /** The rail's live signal for this avatar (the same one behind its dot). */
  online?: boolean;
  /** The avatar that opened the card; the card opens to its right and follows it as the rail re-indexes. */
  trigger: HTMLElement;
  /** Changes whenever the rail's order changes, so the card re-measures the trigger. */
  layoutKey: string;
  /** Closes the card. `restoreFocus` is true for Escape and false when a link or outside click closed it. */
  onClose: (restoreFocus: boolean) => void;
};

/** A small popover that summarises one person or agent from the presence rail. Uses only data the hub already serves. */
export function PersonCard({ actorId, online, trigger, layoutKey, onClose }: PersonCardProps) {
  const [anchor, setAnchor] = useState(() => trigger.getBoundingClientRect());
  // The rail is fixed, so its rect never changes on resize; the viewport height is what the clamp needs to follow.
  const [viewportHeight, setViewportHeight] = useState(() => window.innerHeight);
  useEffect(() => {
    const onResize = () => setViewportHeight(window.innerHeight);
    window.addEventListener("resize", onResize);
    return () => window.removeEventListener("resize", onResize);
  }, []);
  // Tiles slide when the rail re-indexes, so keep measuring for a moment after a layout change and on resize.
  useEffect(() => {
    let raf = 0;
    const measure = () => {
      // A trigger that left the DOM (pushed into "+N") or is hidden (rail gone below lg) has no place to anchor to.
      if (!trigger.isConnected || trigger.getClientRects().length === 0) return onClose(false);
      const next = trigger.getBoundingClientRect();
      setAnchor((prev) => (prev.top === next.top && prev.right === next.right ? prev : next));
    };
    const end = performance.now() + 800;
    const tick = () => {
      measure();
      if (performance.now() < end) raf = requestAnimationFrame(tick);
    };
    tick();
    window.addEventListener("resize", measure);
    return () => {
      cancelAnimationFrame(raf);
      window.removeEventListener("resize", measure);
    };
  }, [trigger, layoutKey, onClose]);
  const actors = useActors().data ?? [];
  const tasksQuery = useTasks();
  const tasks = tasksQuery.data ?? [];
  // Until the task list lands, "nothing open" would be a lie, so the task sections say "Loading…".
  const tasksState: TasksState = tasksQuery.data !== undefined ? "ready" : tasksQuery.isError ? "error" : "loading";
  const lanes = useBridge().data?.agentLanes ?? [];
  const ref = useRef<HTMLElement>(null);

  const actor = actors.find((a) => a.id === actorId);

  // Keyboard users land inside the card once it has content; Escape and outside clicks dismiss it.
  useEffect(() => {
    if (actor) ref.current?.focus();
  }, [actor?.id]);

  useEffect(() => {
    const onKey = (event: KeyboardEvent) => {
      if (event.key !== "Escape" || event.defaultPrevented) return;
      // Another overlay or a field owns Escape unless focus is on the card, an avatar, or nowhere in particular.
      const focus = document.activeElement;
      // Focus that wandered elsewhere on the page (tabbed past the card) still dismisses it; only a text field or another dialog keeps Escape.
      const ownedElsewhere =
        focus instanceof Element &&
        !ref.current?.contains(focus) &&
        (focus.matches("input, textarea, select, [contenteditable=true]") || Boolean(focus.closest('[role="dialog"], [aria-modal="true"]')));
      if (ownedElsewhere) return;
      event.preventDefault();
      onClose(true);
    };
    const onPointer = (event: PointerEvent) => {
      const target = event.target;
      if (!(target instanceof Node)) return;
      if (ref.current?.contains(target)) return;
      // Avatars toggle or switch the card themselves; any other click, rail padding included, dismisses it.
      if (target instanceof Element && target.closest('[data-testid="presence-avatar"]')) return;
      onClose(false);
    };
    document.addEventListener("keydown", onKey);
    document.addEventListener("pointerdown", onPointer);
    return () => {
      document.removeEventListener("keydown", onKey);
      document.removeEventListener("pointerdown", onPointer);
    };
  }, [onClose]);

  if (!actor) return null;

  const close = () => onClose(false);
  const isAgent = actor.kind === "agent";
  const isOnline = online ?? actor.online;
  const top = Math.max(12, Math.min(anchor.top, viewportHeight - 200));

  // Portaled to <body>: the rail is a fixed z-20 stacking context, which would pin the card under the z-30 header.
  return createPortal(
    <section
      ref={ref}
      role="dialog"
      aria-label={`${displayName(actor.name)}, summary`}
      data-testid="person-card"
      tabIndex={-1}
      onBlur={(event) => {
        // Tabbing out of the dialog dismisses it, so Escape never has to chase a card focus already left.
        const next = event.relatedTarget;
        if (!(next instanceof Element) || ref.current?.contains(next)) return;
        if (next.closest('[data-testid="presence-avatar"]')) return;
        onClose(false);
      }}
      style={{ left: anchor.right + 12, top, width: CARD_WIDTH, maxHeight: `calc(100vh - ${top + 12}px)` }}
      className="fixed z-40 overflow-y-auto rounded-2xl border border-rule/50 bg-card/95 p-4 text-[13px] shadow-lg outline-none backdrop-blur-xl"
    >
      <button
        type="button"
        onClick={() => onClose(true)}
        aria-label="Close summary"
        data-testid="person-card-close"
        className="absolute right-2 top-2 grid size-6 place-items-center rounded-full text-ink-3 hover:bg-paper-2 hover:text-ink focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-[color:var(--focus)]"
      >
        <span aria-hidden>×</span>
      </button>
      {isAgent ? (
        <AgentBody actor={actor} actors={actors} tasks={tasks} tasksState={tasksState} lane={lanes.find((l) => l.actorId === actor.id) ?? null} online={isOnline} close={close} />
      ) : (
        <PersonBody actor={actor} actors={actors} tasks={tasks} tasksState={tasksState} online={isOnline} close={close} />
      )}
    </section>,
    document.body,
  );
}

type BodyProps = {
  actor: ActorDTO;
  actors: ActorDTO[];
  tasks: TaskDTO[];
  tasksState: TasksState;
  online: boolean;
  close: () => void;
};

function PersonBody({ actor, actors, tasks, tasksState, online, close }: BodyProps) {
  const agents = actors.filter((a) => a.kind === "agent" && a.active && a.ownerId === actor.id);
  const openTasks = openTasksFor(actor, actors, tasks);
  return (
    <>
      <header>
        <p className="text-[15px] font-medium text-ink" data-testid="person-card-name">
          {displayName(actor.name)}
        </p>
        <p className="mt-0.5 text-ink-3" data-testid="person-card-role">
          {roleLabel(actor.role)}
        </p>
        <div className="mt-1.5">
          <Presence online={online} lastSeenAt={actor.lastSeenAt} />
        </div>
      </header>

      <div className="mt-4">
        <SectionLabel>Agents</SectionLabel>
        {agents.length === 0 ? (
          <p className="text-ink-3">No agents yet.</p>
        ) : (
          <ul className="space-y-1" data-testid="person-card-agents">
            {agents.map((agent) => (
              <li key={agent.id} className="flex items-center gap-2 text-ink-2">
                <StatusDot online={agent.online} />
                <span className="min-w-0 flex-1 truncate text-ink">{agent.name}</span>
                <span className="text-[11px] text-ink-3" data-testid="person-card-agent-status" data-online={agent.online ? "true" : "false"}>
                  {agent.online ? "Online" : "Offline"}
                </span>
              </li>
            ))}
          </ul>
        )}
      </div>

      <div className="mt-4">
        <SectionLabel>Open tasks</SectionLabel>
        {openTasks.length === 0 ? (
          <p className="text-ink-3">{tasksMessage(tasksState, "Nothing open.")}</p>
        ) : (
          <ul className="space-y-0.5" data-testid="person-card-tasks">
            {openTasks.map((task) => (
              <TaskLink key={task.id} task={task} onNavigate={close} />
            ))}
          </ul>
        )}
      </div>
    </>
  );
}

function AgentBody({
  actor,
  actors,
  tasks,
  tasksState,
  lane,
  online,
  close,
}: BodyProps & { lane: { taskId: string; taskNumber: number; taskTitle: string } | null }) {
  const owner = actors.find((a) => a.id === actor.ownerId) ?? null;
  // The lane is the live signal; without one, prefer the task it is actively building over other open work.
  const current = currentTaskFor(actor, tasks, lane?.taskId ?? null);
  const currentView = current ?? (lane ? { id: lane.taskId, number: lane.taskNumber, title: lane.taskTitle } : null);
  return (
    <>
      <header>
        <p className="text-[15px] font-medium text-ink" data-testid="person-card-name">
          {displayName(actor.name)}
        </p>
        <p className="mt-0.5 text-ink-3" data-testid="person-card-role">
          Agent{owner ? <> · owned by <span className="text-ink-2">{owner.name}</span></> : null}
        </p>
        <div className="mt-1.5">
          <Presence online={online} lastSeenAt={actor.lastSeenAt} />
        </div>
      </header>

      <div className="mt-4">
        <SectionLabel>Current task</SectionLabel>
        {currentView ? (
          <ul data-testid="person-card-tasks">
            {current ? (
              <TaskLink task={current} onNavigate={close} />
            ) : (
              <li>
                <Link to={`/tasks/${currentView.id}`} onClick={close} data-testid="person-card-task" className="flex items-baseline gap-2 rounded-md px-1.5 py-1 -mx-1.5 text-[13px] text-ink hover:bg-paper-2 focus-visible:bg-paper-2 focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-[color:var(--focus)]">
                  <span className="font-mono text-[11px] tabular-nums text-ink-3">#{currentView.number}</span>
                  <span className="min-w-0 flex-1 truncate">{currentView.title}</span>
                </Link>
              </li>
            )}
          </ul>
        ) : (
          <p className="text-ink-3">{tasksMessage(tasksState, "Idle, no task right now.")}</p>
        )}
      </div>
    </>
  );
}
